import { useEffect, useState } from 'react';
import { api } from '../../../../services/api';
import { alternarRodovia, rodoviasNoItinerario } from './itinerario';
import { kmFaturavelDe } from './valor';
import { erroDoTrajeto } from './validacao';
import type { RotaResposta } from './types';

const ITINERARIO_MAX = 200;

interface Props {
  cMunIni: string;
  cMunFim: string;
  somenteIda: boolean;
  permitirSomenteIda: boolean;
  permitirReduzirKm: boolean;
  kmFiscalIncluiVolta: boolean;
  kmIda: number;
  kmVolta: number;
  itinerario: string;
  onSomenteIda: (v: boolean) => void;
  onKm: (ida: number, volta: number, idaBase: number, voltaBase: number) => void;
  onItinerario: (v: string) => void;
}

/**
 * Passo 5 — handoff §2. Dois mecanismos de limite de 200 chars: digitação
 * livre trunca no maxLength do campo (bloqueio duro); toque em chip de
 * rodovia AVISA em vez de truncar (itinerario.ts#alternarRodovia) — a
 * frase "Avisa em vez de truncar" do handoff vale só para o segundo
 * caminho (research.md, wizard divergência #4).
 */
export function PassoTrajeto({
  cMunIni, cMunFim, somenteIda, permitirSomenteIda, permitirReduzirKm, kmFiscalIncluiVolta,
  kmIda, kmVolta, itinerario,
  onSomenteIda, onKm, onItinerario,
}: Props) {
  const [rota, setRota] = useState<RotaResposta | null>(null);
  const [avisoLimite, setAvisoLimite] = useState<string | null>(null);
  // A falha da consulta era calada: o km ficava zerado e nada dizia por quê.
  const [rotaFalhou, setRotaFalhou] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    if (!cMunIni || !cMunFim) return;
    setRotaFalhou(false);
    api.get<RotaResposta>('/cooperado/atf/rota', { params: { origemIbge: cMunIni, destinoIbge: cMunFim } })
      .then(res => {
        setRota(res.data);
        onKm(res.data.kmIda, res.data.kmVolta, res.data.kmIda, res.data.kmVolta);
        if (!itinerario) onItinerario(res.data.itinerarioSugerido);
      })
      .catch(() => { setRota(null); setRotaFalhou(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cMunIni, cMunFim, tentativa]);

  const rodoviasAtivas = rodoviasNoItinerario(itinerario);
  const kmTotal = kmFaturavelDe({ kmIda, kmVolta, somenteIda, kmFiscalIncluiVolta });

  function toggleRodovia(sigla: string) {
    const nova = alternarRodovia(itinerario, sigla, ITINERARIO_MAX);
    if (nova === null) {
      setAvisoLimite(`O itinerário chegou ao limite de ${ITINERARIO_MAX} caracteres. Remova alguma via antes de incluir outra.`);
      return;
    }
    setAvisoLimite(null);
    onItinerario(nova);
  }

  const erro = erroDoTrajeto({
    kmIda, kmVolta, kmIdaBase: rota?.kmIda ?? 0, kmVoltaBase: rota?.kmVolta ?? 0,
    somenteIda, permitirReduzirKm, itinerario,
  });

  return (
    <div className="co-pilha">
      {permitirSomenteIda && (
        <div className="co-campo" role="radiogroup" aria-labelledby="co-trajeto-sentido">
          <span id="co-trajeto-sentido" className="co-campo-rotulo">Sentido</span>
          <div className="toggle-pill">
            <input type="radio" id="co-sentido-ida-volta" name="co-sentido" checked={!somenteIda} onChange={() => onSomenteIda(false)} />
            <label htmlFor="co-sentido-ida-volta">Ida e volta</label>
            <input type="radio" id="co-sentido-ida" name="co-sentido" checked={somenteIda} onChange={() => onSomenteIda(true)} />
            <label htmlFor="co-sentido-ida">Somente ida</label>
          </div>
        </div>
      )}

      {rotaFalhou && (
        <div className="co-nota co-nota-atencao" role="alert">
          Não foi possível consultar a distância. Informe o km da ida{somenteIda ? '' : ' e da volta'}, ou tente de novo.
          <div className="co-acoes">
            <button type="button" className="co-btn co-btn-secundario" onClick={() => setTentativa(t => t + 1)}>
              Consultar de novo
            </button>
          </div>
        </div>
      )}

      <div className="co-km">
        <label className="co-campo">
          <span className="co-campo-rotulo">Km de ida</span>
          <input
            className="co-entrada co-entrada-curta co-numero"
            type="number" inputMode="numeric" value={kmIda || ''}
            onChange={e => onKm(Number(e.target.value), kmVolta, rota?.kmIda ?? 0, rota?.kmVolta ?? 0)}
          />
          {rota && <span className="co-campo-ajuda">mínimo {rota.kmIda} km</span>}
        </label>
        {!somenteIda && (
          <label className="co-campo">
            <span className="co-campo-rotulo">Km de volta</span>
            <input
              className="co-entrada co-entrada-curta co-numero"
              type="number" inputMode="numeric" value={kmVolta || ''}
              onChange={e => onKm(kmIda, Number(e.target.value), rota?.kmIda ?? 0, rota?.kmVolta ?? 0)}
            />
            {rota && <span className="co-campo-ajuda">mínimo {rota.kmVolta} km</span>}
          </label>
        )}
      </div>

      {/* O total é o número que vai ao documento e à cobrança — e até 18/09/2026
          não aparecia em lugar nenhum da tela. O chamado que originou a spec 0039
          chegou como "está retornando 46 km" porque o cooperado via 23 e 23. */}
      {kmTotal > 0 && (
        <div className="co-total">
          <span className="co-rotulo">Total no documento</span>
          <strong>{kmTotal} km</strong>
          {!somenteIda && kmFiscalIncluiVolta && <span className="co-apoio">ida e volta somadas</span>}
        </div>
      )}

      {/* `estimado` existe no contrato desde a criação do endpoint e nenhum
          consumidor o lia: quando o roteador cai, o km vira linha reta e o
          cooperado assinava sem saber. */}
      {rota?.estimado && (
        <p className="co-nota co-nota-atencao" role="status">
          Distância aproximada: não foi possível calcular a rota por estrada agora.
          Confira o trajeto e ajuste o km antes de emitir.
        </p>
      )}

      {rota && rota.rodovias.length > 0 && (
        <div className="co-campo">
          <span className="co-campo-rotulo" id="co-rodovias">Vias do trajeto</span>
          <div className="fc-chips" role="group" aria-labelledby="co-rodovias">
            {rota.rodovias.map(r => {
              const ativa = rodoviasAtivas.has(r.toUpperCase().replace(/[^A-Z0-9]/g, ''));
              return (
                <button
                  key={r}
                  type="button"
                  className={`fc-chip${ativa ? ' fc-chip-ativo' : ''}`}
                  aria-pressed={ativa}
                  onClick={() => toggleRodovia(r)}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <label className="co-campo">
        <span className="co-campo-rotulo">Itinerário</span>
        <textarea
          className="co-entrada"
          value={itinerario}
          onChange={e => onItinerario(e.target.value.slice(0, ITINERARIO_MAX))}
          maxLength={ITINERARIO_MAX}
          rows={3}
        />
        <span className="co-contador">{itinerario.length}/{ITINERARIO_MAX}</span>
      </label>

      {avisoLimite && <p className="co-nota" role="status">{avisoLimite}</p>}
      {erro && <p className="co-apoio" role="alert">{erro}</p>}
    </div>
  );
}
